import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { getCardApplications, assignCardNumber } from '../../../services/cardService';
import { BASE_URL } from '../../../services/api';
import { isAdminUser } from '../../../services/authService';
import { message } from 'antd'; // Add antd message

const CardApplicationsList = ({ showToast }) => {
    const isAdmin = isAdminUser();
    const [applications, setApplications] = useState([]);
    const [selectedApp, setSelectedApp] = useState(null);
    const [viewDetailsApp, setViewDetailsApp] = useState(null);
    const [fullScreenPhoto, setFullScreenPhoto] = useState(null);
    const [cardNumber, setCardNumber] = useState('');
    
    // Pagination & Sorting & Filter State
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest' | 'oldest'
    const [searchQuery, setSearchQuery] = useState('');
    const itemsPerPage = 15;

    useEffect(() => {
        loadData();
    }, []);

    // ESC ile modal kapatma
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key !== 'Escape') return;
            if (fullScreenPhoto) { setFullScreenPhoto(null); return; }
            if (viewDetailsApp) { setViewDetailsApp(null); return; }
            if (selectedApp) { setSelectedApp(null); return; }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [fullScreenPhoto, viewDetailsApp, selectedApp]);

    const loadData = async () => {
        try {
            const res = await getCardApplications();
            if (res.data) setApplications(res.data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleAssign = async () => {
        if (!selectedApp || !cardNumber) return;
        try {
            const res = await assignCardNumber({ applicationId: selectedApp.id, cardNumber });
            if (res.isSuccess) {
                if (showToast) {
                    showToast('Kart başarıyla atandı!', 'success');
                } else {
                    message.success("Kart atandı!");
                }
                setSelectedApp(null);
                setCardNumber('');
                loadData();
            } else {
                if (showToast) {
                    showToast(res.resultMessage || "Bir hata oluştu.", 'error');
                } else {
                    message.error(res.resultMessage || "Hata oluştu.");
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const filteredApplications = React.useMemo(() => {
        let filtered = [...applications];

        // Search Filtering
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            filtered = filtered.filter(app =>
                `${app.firstName} ${app.lastName}`.toLowerCase().includes(q) ||
                (app.identityNumber || '').includes(q) ||
                (app.phone || '').includes(q)
            );
        }

        // Sorting
        filtered.sort((a, b) => {
            const dateA = new Date(a.createdDate).getTime();
            const dateB = new Date(b.createdDate).getTime();
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

        return filtered;
    }, [applications, searchQuery, sortOrder]);

    const paginatedApplications = React.useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredApplications.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredApplications, currentPage]);

    const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

    // Reset pagination on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, sortOrder]);

    return (
        <div className="appointments-list-section card-applications-view">
            <div className="section-header">
                <div className="section-header-left">
                    <h3>Kart Başvuruları</h3>
                    <span className="appointment-count">{filteredApplications.length} Başvuru</span>
                </div>
                <div className="section-header-right">
                    <button className="refresh-button" onClick={loadData}>
                        Yenile
                    </button>
                </div>
            </div>

            <div className="appointments-filter-bar">
                <div className="filter-search">
                    <input
                        type="text"
                        placeholder="İsim, TC veya Telefon ile ara..."
                        className="filter-search-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <select 
                        className="sort-select" 
                        value={sortOrder} 
                        onChange={(e) => setSortOrder(e.target.value)}
                    >
                        <option value="newest">En Yeni Önce</option>
                        <option value="oldest">En Eski Önce</option>
                    </select>
                </div>
            </div>

            <div className="appointments-table-wrapper">
                <table className="appointments-table">
                    <thead>
                        <tr>
                            <th>Ad Soyad</th>
                            <th>TC</th>
                            <th>Tel</th>
                            <th>Ödenen Ücret</th>
                            <th>Durum</th>
                            <th>Kart No</th>
                            <th>İşlem</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedApplications.map(app => (
                            <tr key={app.id}>
                                <td className="name-cell">{app.firstName} {app.lastName}</td>
                                <td>{app.identityNumber}</td>
                                <td>{app.phone}</td>
                                <td className="payment-cell">
                                    <span className="payment-amount">
                                        {Number(app.paidAmount || 0).toLocaleString('tr-TR')} ₺
                                    </span>
                                </td>
                                <td>
                                    <span className={`status-badge ${app.status?.toLowerCase().replace(/\s+/g, '')}`}>
                                        {app.status}
                                    </span>
                                </td>
                                <td style={{ fontStyle: app.cardNumber ? 'normal' : 'italic', color: app.cardNumber ? 'inherit' : '#94a3b8' }}>
                                    {app.cardNumber || 'Tanımlanmadı'}
                                </td>
                                <td className="appointment-actions">
                                    <button
                                        className="action-button detail"
                                        onClick={() => setViewDetailsApp(app)}
                                        title="Detayları Görüntüle"
                                    >
                                        Detay
                                    </button>
                                    {!app.cardNumber && (
                                        <button
                                            className="action-button transfer"
                                            onClick={() => {
                                                if (!isAdmin) {
                                                    if (showToast) {
                                                        showToast('Bu işlem için yöneticiniz ile görüşün.', 'error');
                                                    } else {
                                                        message.error('Bu işlem için yöneticiniz ile görüşün.');
                                                    }
                                                    return;
                                                }
                                                setSelectedApp(app);
                                            }}
                                            title="Kart Numarası Ata"
                                        >
                                            Kart Tanımla
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {filteredApplications.length === 0 && (
                            <tr>
                                <td colSpan="7" className="no-data">Başvuru bulunamadı.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="pagination-wrapper">
                    <button 
                        className="pagination-btn pagination-nav-btn" 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        title="Önceki Sayfa"
                    >
                        <FaChevronDown style={{ transform: 'rotate(90deg)' }} />
                    </button>
                    
                    <div className="pagination-pages">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                                return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                            })
                            .map((page, index, array) => (
                                <React.Fragment key={page}>
                                    {index > 0 && array[index - 1] !== page - 1 && <span className="pagination-ellipsis">...</span>}
                                    <button 
                                        className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                </React.Fragment>
                            ))
                        }
                    </div>

                    <button 
                        className="pagination-btn pagination-nav-btn" 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        title="Sonraki Sayfa"
                    >
                        <FaChevronRight />
                    </button>
                </div>
            )}

            {/* Card Assignment Modal */}
            {selectedApp && (
                <div className="premium-modal-overlay">
                    <div className="premium-modal card-assignment-modal">
                        <div className="modal-header">
                            <h3>Kart Tanımla</h3>
                            <button className="close-btn" onClick={() => setSelectedApp(null)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="user-info-banner">
                                <div className="user-avatar-mini">
                                    {selectedApp.firstName[0]}{selectedApp.lastName[0]}
                                </div>
                                <div className="user-details-mini">
                                    <span className="user-name">{selectedApp.firstName} {selectedApp.lastName}</span>
                                    <span className="user-id">{selectedApp.identityNumber}</span>
                                </div>
                            </div>
                            
                            <div className="input-group">
                                <label>Kart Numarası</label>
                                <input
                                    type="text"
                                    placeholder="000/0000000"
                                    value={cardNumber}
                                    onChange={(e) => setCardNumber(e.target.value)}
                                    autoFocus
                                />
                                <small>Lütfen fiziksel kartın üzerindeki numarayı giriniz.</small>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setSelectedApp(null)}>İptal</button>
                            <button className="btn-primary" onClick={handleAssign}>Kaydet ve Aktif Et</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Application Detail Modal */}
            {viewDetailsApp && (
                <div className="premium-modal-overlay">
                    <div className="premium-modal detail-modal">
                        <div className="modal-header">
                            <h3 className="modal-title">Başvuru Detayı</h3>
                            <span className="modal-subtitle">Kayıt ID: #{viewDetailsApp.id}</span>
                            <button className="close-btn" onClick={() => setViewDetailsApp(null)}>&times;</button>
                        </div>

                        <div className="modal-content-grid">
                            <div className="modal-sidebar">
                                {viewDetailsApp.photoUrl && (() => {
                                    const photoSrc = viewDetailsApp.photoUrl.startsWith('http') ? viewDetailsApp.photoUrl : `${BASE_URL}${viewDetailsApp.photoUrl.startsWith('/') ? '' : '/'}${viewDetailsApp.photoUrl}`;
                                    return (
                                        <div className="profile-photo-container">
                                            <img
                                                src={photoSrc}
                                                alt="Profil"
                                                className="profile-photo"
                                                onClick={() => setFullScreenPhoto(photoSrc)}
                                            />
                                            <div className="photo-overlay">
                                                <span>Büyütmek için tıklayın</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                                <div className="status-indicator">
                                    <label>Başvuru Durumu</label>
                                    <span className={`status-badge ${viewDetailsApp.status?.toLowerCase().replace(/\s+/g, '')}`}>
                                        {viewDetailsApp.status}
                                    </span>
                                </div>
                            </div>

                            <div className="modal-main-info">
                                <div className="info-grid">
                                    <div className="info-item">
                                        <label>Ad Soyad</label>
                                        <p>{viewDetailsApp.firstName} {viewDetailsApp.lastName}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>TC Kimlik</label>
                                        <p>{viewDetailsApp.identityNumber}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Cinsiyet / Yaş</label>
                                        <p>
                                            {viewDetailsApp.userGender === 'Male' ? 'Erkek' : viewDetailsApp.userGender === 'Female' ? 'Kadın' : viewDetailsApp.userGender}
                                            {viewDetailsApp.birthDate && ` / ${new Date().getFullYear() - new Date(viewDetailsApp.birthDate).getFullYear()} Yaş`}
                                        </p>
                                    </div>
                                    <div className="info-item">
                                        <label>Doğum Tarihi</label>
                                        <p>{viewDetailsApp.birthDate ? new Date(viewDetailsApp.birthDate).toLocaleDateString('tr-TR') : '-'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Telefon</label>
                                        <p>{viewDetailsApp.phone}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Yedek Telefon</label>
                                        <p>{viewDetailsApp.phone2 || '-'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>E-Posta</label>
                                        <p>{viewDetailsApp.email || '-'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Meslek</label>
                                        <p>{viewDetailsApp.profession || '-'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Üyelik Süresi</label>
                                        <p>{viewDetailsApp.subscriptionDuration || '-'}</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Kayıt Tarihi</label>
                                        <p>{new Date(viewDetailsApp.createdDate).toLocaleString('tr-TR')}</p>
                                    </div>
                                    <div className="info-item full-width">
                                        <label>Ev Adresi</label>
                                        <div className="address-box">{viewDetailsApp.homeAddress || '-'}</div>
                                    </div>
                                    <div className="info-item">
                                        <label>Ödenen Ücret</label>
                                        <p className="highlight-price">{Number(viewDetailsApp.paidAmount || 0).toLocaleString('tr-TR')} ₺</p>
                                    </div>
                                    <div className="info-item">
                                        <label>Kart Numarası</label>
                                        <p>{viewDetailsApp.cardNumber || 'Atanmadı'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="btn-close" onClick={() => setViewDetailsApp(null)}>Kapat</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Full Screen Photo Viewer */}
            {fullScreenPhoto && (
                <div className="photo-viewer-overlay" onClick={() => setFullScreenPhoto(null)}>
                    <div className="photo-viewer-content" onClick={e => e.stopPropagation()}>
                        <button className="viewer-close" onClick={() => setFullScreenPhoto(null)}>&times;</button>
                        <img src={fullScreenPhoto} alt="Vesikalık" className="full-size-photo" />
                        <div className="viewer-actions">
                            <button
                                className="btn-download"
                                onClick={() => {
                                    let downloadBaseUrl = BASE_URL;
                                    if (!downloadBaseUrl.endsWith('/api')) downloadBaseUrl += '/api';
                                    const downloadUrl = `${downloadBaseUrl}/card/download-photo?url=${encodeURIComponent(fullScreenPhoto)}`;
                                    const a = document.createElement('a');
                                    a.href = downloadUrl;
                                    a.download = `Vesikalik_${viewDetailsApp?.identityNumber}.jpg`;
                                    a.target = '_blank';
                                    a.click();
                                }}
                            >
                                Fotoğrafı İndir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CardApplicationsList;
